import type { Locale } from "~/utils/locale";

/**
 * SINGLE SOURCE OF TRUTH for path-prefixed partner markets.
 *
 * A path-prefixed market is a partner storefront served under a URL
 * prefix on the main domain (eql-apparel.com/th → Thailand). Launching
 * another one (Singapore, Malaysia…) means:
 *
 *   1. Add one entry here: segment → { country, defaultLocale }.
 *   2. Mirror it in server.mjs PATH_PREFIX_MARKETS — that file boots
 *      before the build exists so it cannot import this module;
 *      marketSegments.test.ts asserts the two stay in sync.
 *   3. Everything else derives from this map: the layout's 404 guard
 *      and chrome default language, the fb-capi partner skip, and the
 *      per-market feed link prefix.
 *
 * Full replication runbook: docs/plans/2026-07-03-market-partner-playbook.md
 *
 * defaultLocale is the CHROME language a first-time visitor sees on the
 * market surface before choosing one ("th" → Thai UI strings from
 * translationsTh.ts). For an English-default market (Singapore) use
 * "en" — the header language switcher toggles market-default ↔ EN, so
 * an "en" market renders it degenerate; hide it for that market when
 * the time comes.
 */
export const PATH_PREFIX_MARKETS: Record<
  string,
  { country: string; defaultLocale: Locale }
> = {
  th: { country: "TH", defaultLocale: "th" },
};

/** Valid values of the ($market) optional route segment. */
export const VALID_MARKET_SEGMENTS = new Set(Object.keys(PATH_PREFIX_MARKETS));

/** Matches any market-prefixed path: /th, /th/, /th/products… */
export const MARKET_PATH_REGEX = new RegExp(
  `^/(?:${Object.keys(PATH_PREFIX_MARKETS).join("|")})(/|$)`,
);

/**
 * "/th" for a market whose countries include TH; "" when the market has
 * no path-prefixed surface. Used by the product feeds to build market
 * links from the Market row's countries JSON.
 */
export function pathPrefixForCountries(countries: unknown): string {
  if (!Array.isArray(countries)) return "";
  for (const [segment, def] of Object.entries(PATH_PREFIX_MARKETS)) {
    if (countries.includes(def.country)) return `/${segment}`;
  }
  return "";
}

/** Default chrome locale for a market path prefix ("/th" → "th"); null off-market. */
export function defaultLocaleForPrefix(
  prefix: string | null | undefined,
): Locale | null {
  if (!prefix) return null;
  return PATH_PREFIX_MARKETS[prefix.replace(/^\//, "")]?.defaultLocale ?? null;
}
